import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Tab,
  Tabs,
  Button,
  Box,
  Typography,
  Paper,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import NavigationBar from '../components/NavigationBar';
import CollapsibleSidebar from '../components/CollapsibleSidebar';
import FormInputs from '../components/FormInputs';
import UploadCSV from '../components/UploadCSV';
import RetrievalBaseline from '../components/RetrievalBaselineInput';
import {
  fetchUserDetails,
  fetchModels,
  submitBatch,
  checkBatchExists,
} from '../utils/api';
import { handleLogout, getAccessToken } from '../utils/auth';
import TenantSelector from '../components/TenantSelector';

function SubmissionPage() {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode") ?? 'knowledge';
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [tenantIdOptions, setTenantIdOptions] = useState([]);
  const [tenantId, setTenantId] = useState('');
  const [DBType, setDBType] = useState('');
  const [baselineTenants, setBaselineTenants] = useState([]);

  const [modelOptions, setModelOptions] = useState([]);
  const [modelList, setModelList] = useState([]);

  const [numberOfQuestions, setNumberOfQuestions] = useState(2);
  const [selectedDate, setSelectedDate] = useState('');
  const [evaluationCriteria, setEvaluationCriteria] = useState([]);
  const [notifiedUserEmail, setNotifiedUser] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [buttonText, setButtonText] = useState('Loading...');
  const [batchId, setBatchId] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const navigate = useNavigate();

  const title = mode === 'knowledge' ? 'Batch Ask Knowledge' : 'Batch Ask GPT';


  useEffect(() => {
    document.title = 'Success Suite - Submit';
    const accessToken = getAccessToken();

    fetchUserDetails(accessToken)
      .then((data) => {
        if (!data || !data.tenant_id) {
          alert('No tenant associated with your user credentials. Please log in with the correct account.');
          navigate('/login');
          return;
        }

        setUserName(data.user_name);
        setUserEmail(data.user_email);
        setNotifiedUser(data.user_email);
        setTenantIdOptions(data.tenant_id);
        setBaselineTenants(data.retrieval_baseline_tenant || []);

        const keys = Object.keys(data.tenant_id || {});
        if (keys.length === 1 && data.tenant_id[keys[0]].length === 1) {
          const dbType = keys[0];
          const tenantName = data.tenant_id[dbType][0];
          setTenantId(tenantName);
          setDBType(dbType);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        console.error('Error fetching user details:', err.message);
      });
  }, []);

  useEffect(() => {
    if (!tenantId || !DBType) return;

    const accessToken = getAccessToken();
    fetchModels(tenantId, accessToken, DBType)
      .then((data) => {
        setModelOptions(data.models || []);
        setIsLoading(false);
        setButtonText('Submit');
      })
      .catch((err) => {
        console.error('Error fetching models:', err.message);
        setIsLoading(false);
      });
  }, [tenantId, DBType]);

  useEffect(() => {
    if (batchId) {
      navigate(`/results/${batchId}`);
    }
  }, [batchId, navigate]);

  const handleBatchAsk = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(notifiedUserEmail)) {
      alert('Please enter a valid email address for notifications.');
      return;
    }

    const accessToken = sessionStorage.getItem('access_token');
    setIsSubmitting(true);
    setButtonText('Creating question batch...');

    try {
      const result = await submitBatch({
        tenant_list: [tenantId],
        number_of_questions: numberOfQuestions,
        access_token: accessToken,
        model_list: modelList,
        selected_date: selectedDate,
        evaluation_criteria: evaluationCriteria,
        user_email: notifiedUserEmail,
        db_type: DBType,
        mode: mode,
      });
      setBatchId(result.batch_id);
    } catch (err) {
      console.error(err.message);
      setButtonText('Submit');
      setIsSubmitting(false);
    }
  };

  const handleCSVUpload = async ({ preparedData, userEmail, evaluationCriteria }) => {
    const accessToken = sessionStorage.getItem('access_token');
    setIsSubmitting(true);

    try {
      const result = await submitBatch({
        tenant_list: [tenantId],
        csv_questions: preparedData,
        access_token: accessToken,
        user_email: userEmail,
        evaluation_criteria: evaluationCriteria,
        db_type: DBType,
        mode: mode
      });
      setBatchId(result.batch_id);
    } catch (err) {
      console.error(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetrievalEval = async ({ selectedModel, userEmail, evaluationCriteria, selectedBaselineTenant, DBType }) => {
    const accessToken = getAccessToken();
    setIsSubmitting(true);

    try {
      const result = await submitBatch({
        tenant_list: [tenantId],
        model_list: [selectedModel],
        access_token: accessToken,
        user_email: userEmail,
        evaluation_criteria: evaluationCriteria,
        baseline_tenant: selectedBaselineTenant,
        db_type: DBType,
        mode: mode,
      });
      setBatchId(result.batch_id);
    } catch (err) {
      console.error(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CollapsibleSidebar />
      <Box sx={{ flexGrow: 1 }}>
        <NavigationBar
          userName={userName}
          userEmail={userEmail}
          tenantId={tenantId}
          onLogout={handleLogout}
        />

        <Box
          sx={{
            padding: 3,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '90vh',
          }}
        >
          {isLoading ? (
            <Paper
              elevation={3}
              sx={{
                padding: 6,
                width: '100%',
                maxWidth: 600,
                borderRadius: '25px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <CircularProgress size={40} sx={{ mb: 3 }} />
              <Typography variant="h6" align="center">
                Loading tenant info...
              </Typography>
            </Paper>
          ) : (
            <Paper
              elevation={3}
              sx={{
                padding: 4,
                width: '100%',
                maxWidth: 600,
                borderRadius: '25px',
              }}
            >
              {tenantIdOptions && Object.keys(tenantIdOptions).length > 1 && !tenantId && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Please select a tenant before proceeding:
                  </Typography>
                  <TenantSelector
                    tenantOptions={tenantIdOptions}
                    selectedTenant={null}
                    setSelectedTenant={(opt) => {
                      setTenantId(opt?.value?.value);
                      setDBType(opt?.value?.key);
                    }}
                  />
                </Box>
              )}

              {tenantId && (
                <>
                  {DBType === 'prod' && (
                    <Box sx={{
                      backgroundColor: '#ffe5e5',
                      color: '#b00020',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '1px solid #b00020',
                      mb: 2
                    }}>
                      <strong>Warning:</strong> You are working with a <strong>PRODUCTION</strong> tenant. Make sure this is correct.
                    </Box>
                  )}

                  <Typography variant="h5" align="center" gutterBottom>
                    {title}
                  </Typography>

                  <Tabs
                    value={activeTab}
                    onChange={(e, v) => setActiveTab(v)}
                    centered
                    sx={{ marginBottom: 2 }}
                  >
                    <Tab label="Questions from DB" />
                    <Tab label="CSV Upload" />
                    <Tab label="Retrieval Baseline" disabled={mode !== 'knowledge'} />
                  </Tabs>

                  {activeTab === 0 && (
                    <>
                      <FormInputs
                        selectedDate={selectedDate}
                        setSelectedDate={setSelectedDate}
                        tenantList={tenantId}
                        setTenantId={setTenantId}
                        tenantOptions={tenantIdOptions}
                        modelList={modelList}
                        setModelList={setModelList}
                        modelOptions={modelOptions}
                        numberOfQuestions={numberOfQuestions}
                        setNumberOfQuestions={setNumberOfQuestions}
                        notifiedUserEmail={notifiedUserEmail}
                        setNotifiedUser={setNotifiedUser}
                        evaluationCriteria={evaluationCriteria}
                        setEvaluationCriteria={setEvaluationCriteria}
                        DBType={DBType}
                      />

                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={handleBatchAsk}
                        disabled={isSubmitting}
                        sx={{ marginTop: 2 }}
                      >
                        {buttonText}
                      </Button>
                    </>
                  )}

                  {activeTab === 1 && (
                    <UploadCSV
                      handleCSVUpload={handleCSVUpload}
                      isSubmitting={isSubmitting}
                      modelOptions={modelOptions}
                      tenantOptions={tenantId}
                      defaultEmail={notifiedUserEmail}
                      DBType={DBType}
                      mode={mode}
                    />
                  )}

                  {activeTab === 2 && (
                    <RetrievalBaseline
                      handleBatch={handleRetrievalEval}
                      disabled={isSubmitting}
                      tenantOptions={tenantId}
                      setTenantIdOptions={setTenantIdOptions}
                      modelList={modelList}
                      setModelList={setModelList}
                      modelOptions={modelOptions}
                      defaultEmail={notifiedUserEmail}
                      baselineTenantList={baselineTenants}
                      DBType={DBType}
                    />
                  )}
                </>
              )}

              {isSubmitting && (
                <Box mt={2} textAlign="center">
                  <CircularProgress size={24} sx={{ marginBottom: 2 }} />
                  <Typography variant="body1">
                    Your batch is being created...
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Please wait while we process your submission.
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default SubmissionPage;